import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2, Plus, Trash2, X, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MarketplaceGate } from "@/components/marketplace/gate";
import { TagPicker } from "@/components/marketplace/tag-picker";
import { ImageCropDialog } from "@/components/image-crop-dialog";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { fetchMarketplaceCategories } from "@/lib/marketplace/queries";
import { uploadImage } from "@/lib/uploads";
import { useT } from "@/i18n";

export const Route = createFileRoute("/marketplace/services/$serviceId/edit")({
  head: () => ({
    meta: [
      { title: "Edit service | BeHot.Now Marketplace" },
      { name: "description", content: "Update your BeHot.Now marketplace service and resubmit it for review." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: EditServicePage,
});

type PackageDraft = {
  id?: string;
  tier: "basic" | "standard" | "premium";
  title: string;
  description: string;
  priceUsd: string;
  deliveryDays: string;
  revisions: string;
  features: string;
};

const emptyPackage = (tier: PackageDraft["tier"]): PackageDraft => ({
  tier,
  title: "",
  description: "",
  priceUsd: "",
  deliveryDays: "3",
  revisions: "1",
  features: "",
});

function EditServicePage() {
  const { t } = useT();
  const { serviceId } = Route.useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  useEffect(() => {
    if (!loading && !user) void navigate({ to: "/login" });
  }, [loading, user, navigate]);

  const { data: categories } = useQuery({ queryKey: ["mp-categories"], queryFn: fetchMarketplaceCategories });

  const { data: service, isLoading } = useQuery({
    queryKey: ["mp-service-edit", serviceId],
    enabled: Boolean(user?.id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("marketplace_services")
        .select("*")
        .eq("id", serviceId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const { data: pkgRows } = useQuery({
    queryKey: ["mp-service-edit-pkgs", serviceId],
    enabled: Boolean(service?.id),
    queryFn: async () => {
      const { data } = await supabase
        .from("marketplace_service_packages")
        .select("*")
        .eq("service_id", serviceId)
        .order("sort_order");
      return data ?? [];
    },
  });

  const { data: reqRows } = useQuery({
    queryKey: ["mp-service-edit-reqs", serviceId],
    enabled: Boolean(service?.id),
    queryFn: async () => {
      const { data } = await supabase
        .from("marketplace_service_requirements")
        .select("*")
        .eq("service_id", serviceId)
        .order("sort_order");
      return data ?? [];
    },
  });

  const [parentId, setParentId] = useState("");
  const [subId, setSubId] = useState("");
  const [title, setTitle] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [coverUrl, setCoverUrl] = useState("");
  const [gallery, setGallery] = useState<string[]>([]);
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState<"cover" | "gallery" | null>(null);
  const [cropFile, setCropFile] = useState<File | null>(null);
  const [cropKind, setCropKind] = useState<"cover" | "gallery">("cover");
  const [cropOpen, setCropOpen] = useState(false);
  const [packages, setPackages] = useState<PackageDraft[]>([emptyPackage("basic")]);
  const [requirements, setRequirements] = useState<string[]>([""]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (!service || hydrated) return;
    setParentId(service.category_id ?? "");
    setSubId(service.subcategory_id ?? "");
    setTitle(service.title ?? "");
    setShortDescription(service.short_description ?? "");
    setDescription(service.description ?? "");
    setTags((service.tags as string[] | null) ?? []);
    setCoverUrl(service.cover_url ?? "");
    setGallery(((service.gallery as string[] | null) ?? []).filter(Boolean));
    setHydrated(true);
  }, [service, hydrated]);

  useEffect(() => {
    if (!pkgRows?.length) return;
    setPackages(
      pkgRows.map((p) => ({
        id: p.id as string,
        tier: (p.tier as PackageDraft["tier"]) ?? "basic",
        title: (p.name as string) ?? "",
        description: (p.description as string) ?? "",
        priceUsd: String(((p.price_cents as number) ?? 0) / 100),
        deliveryDays: String(p.delivery_days ?? 3),
        revisions: String(p.revisions ?? 0),
        features: (((p.deliverables_json as string[] | null) ?? []) as string[]).join("\n"),
      })),
    );
  }, [pkgRows]);

  useEffect(() => {
    if (!reqRows?.length) return;
    setRequirements(reqRows.map((r) => (r.prompt as string) ?? ""));
  }, [reqRows]);

  const parents = useMemo(() => (categories ?? []).filter((c) => !c.parent_id), [categories]);
  const subs = useMemo(() => (categories ?? []).filter((c) => c.parent_id === parentId), [categories, parentId]);

  const pickImage = (files: FileList | null, kind: "cover" | "gallery") => {
    const file = files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error(t("mpsell.edit.pleaseChooseImage"));
      return;
    }
    setCropKind(kind);
    setCropFile(file);
    setCropOpen(true);
  };

  const handleUpload = async (file: File, kind: "cover" | "gallery") => {
    if (!user) return;
    setUploading(kind);
    const localPreview = URL.createObjectURL(file);
    try {
      const url = await uploadImage(file, user.id, kind === "cover" ? "service-cover" : "service-gallery");
      setPreviews((p) => ({ ...p, [url]: localPreview }));
      if (kind === "cover") setCoverUrl(url);
      else setGallery((prev) => (prev.length >= 6 ? prev : [...prev, url]));
    } catch (e) {
      URL.revokeObjectURL(localPreview);
      toast.error(e instanceof Error ? e.message : t("mpsell.edit.uploadFailed"));
    } finally {
      setUploading(null);
    }
  };

  const updatePackage = (i: number, patch: Partial<PackageDraft>) =>
    setPackages((prev) => prev.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));

  const save = useMutation({
    mutationFn: async (submitForReview: boolean) => {
      if (!user || !service) throw new Error(t("mpsell.edit.notReady"));
      if (title.trim().length < 10) throw new Error(t("mpsell.edit.titleTooShort"));
      if (description.trim().length < 30) throw new Error(t("mpsell.edit.descTooShort"));
      const basic = packages.find((p) => p.tier === "basic");
      if (!basic?.priceUsd) throw new Error(t("mpsell.edit.basicPriceRequired"));

      const nextStatus = submitForReview ? "pending_moderation" : "draft";

      const { error } = await supabase
        .from("marketplace_services")
        .update({
          title: title.trim(),
          category_id: parentId || null,
          subcategory_id: subId || null,
          short_description: shortDescription || null,
          description,
          tags,
          cover_url: coverUrl || null,
          gallery,
          starting_price_cents: Math.round(Number(basic.priceUsd) * 100),
          delivery_days: Math.min(...packages.map((p) => Number(p.deliveryDays) || 1)),
          status: nextStatus,
          moderation_notes: null,
        })
        .eq("id", service.id);
      if (error) throw error;

      // Replace packages and requirements with the edited set.
      await supabase.from("marketplace_service_packages").delete().eq("service_id", service.id);
      const pkgInsert = packages
        .filter((p) => p.title && p.priceUsd)
        .map((p, i) => ({
          service_id: service.id,
          tier: p.tier,
          name: p.title,
          description: p.description,
          price_cents: Math.round(Number(p.priceUsd) * 100),
          delivery_days: Math.max(1, Number(p.deliveryDays) || 1),
          revisions: Number(p.revisions) || 0,
          deliverables_json: p.features.split("\n").map((f) => f.trim()).filter(Boolean),
          sort_order: i,
        }));
      if (pkgInsert.length) {
        const { error: pkgErr } = await supabase.from("marketplace_service_packages").insert(pkgInsert);
        if (pkgErr) throw pkgErr;
      }

      await supabase.from("marketplace_service_requirements").delete().eq("service_id", service.id);
      const reqInsert = requirements
        .map((r, i) => ({ service_id: service.id, prompt: r.trim(), sort_order: i }))
        .filter((r) => r.prompt);
      if (reqInsert.length) await supabase.from("marketplace_service_requirements").insert(reqInsert);
      return nextStatus;
    },
    onSuccess: (nextStatus) => {
      void qc.invalidateQueries({ queryKey: ["mp-my-services"] });
      toast.success(
        nextStatus === "pending_moderation"
          ? t("mpsell.edit.toastPaused")
          : t("mpsell.edit.toastDraft"),
      );
      void navigate({ to: "/marketplace/sell" });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : t("mpsell.edit.saveError")),
  });

  if (!user) return null;

  if (isLoading) {
    return (
      <MarketplaceGate>
        <div className="mx-auto max-w-3xl px-4 py-16 text-center text-sm text-muted-foreground">{t("mpsell.edit.loading")}</div>
      </MarketplaceGate>
    );
  }

  if (!service || service.seller_user_id !== user.id) {
    return (
      <MarketplaceGate>
        <div className="mx-auto max-w-xl px-4 py-16 text-center">
          <h1 className="font-display text-2xl font-bold">{t("mpsell.edit.notFoundTitle")}</h1>
          <Button asChild variant="hot" className="mt-6">
            <a href="/marketplace/sell">{t("mpsell.edit.backToDashboard")}</a>
          </Button>
        </div>
      </MarketplaceGate>
    );
  }

  return (
    <MarketplaceGate>
      <div className="mx-auto max-w-3xl px-4 py-10">
        <h1 className="font-display text-3xl font-bold">{t("mpsell.edit.pageTitle")}</h1>
        <div className="card-surface mt-4 flex gap-3 p-4">
          <AlertTriangle className="mt-0.5 size-5 shrink-0 text-primary" />
          <p className="text-sm text-muted-foreground">
            {t("mpsell.edit.warning")}
          </p>
        </div>

        <div className="mt-6 space-y-6">
          <section className="card-surface space-y-4 p-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label>{t("mpsell.edit.category")}</Label>
                <Select
                  value={parentId}
                  onValueChange={(v) => {
                    setParentId(v);
                    setSubId("");
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("mpsell.edit.chooseCategory")} />
                  </SelectTrigger>
                  <SelectContent>
                    {parents.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("mpsell.edit.subcategory")}</Label>
                <Select value={subId} onValueChange={setSubId} disabled={!parentId}>
                  <SelectTrigger>
                    <SelectValue placeholder={t("mpsell.edit.chooseSubcategory")} />
                  </SelectTrigger>
                  <SelectContent>
                    {subs.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="title">{t("mpsell.edit.title")}</Label>
              <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} />
            </div>
            <div>
              <Label htmlFor="short">{t("mpsell.edit.shortDescription")}</Label>
              <Input
                id="short"
                value={shortDescription}
                onChange={(e) => setShortDescription(e.target.value)}
                maxLength={160}
              />
            </div>
            <div>
              <Label htmlFor="desc">{t("mpsell.edit.description")}</Label>
              <Textarea id="desc" rows={8} value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
            <div>
              <Label>{t("mpsell.edit.tags")}</Label>
              <TagPicker value={tags} onChange={setTags} parentSlug={parents.find((c) => c.id === parentId)?.slug ?? null} max={10} />
            </div>
          </section>

          <section className="card-surface space-y-4 p-5">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-bold">{t("mpsell.edit.packages")}</h2>
              {packages.length < 3 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setPackages((prev) => [
                      ...prev,
                      emptyPackage(prev.length === 1 ? "standard" : "premium"),
                    ])
                  }
                >
                  <Plus className="size-4" /> {t("mpsell.edit.addTier")}
                </Button>
              )}
            </div>
            {packages.map((p, i) => (
              <div key={p.id ?? `${p.tier}-${i}`} className="space-y-3 rounded-lg border border-border p-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold capitalize">{p.tier}</p>
                  {i > 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setPackages((prev) => prev.filter((_, idx) => idx !== i))}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  )}
                </div>
                <Input
                  placeholder={t("mpsell.edit.packageNamePlaceholder")}
                  value={p.title}
                  onChange={(e) => updatePackage(i, { title: e.target.value })}
                />
                <Textarea
                  rows={2}
                  placeholder={t("mpsell.edit.whatsIncludedPlaceholder")}
                  value={p.description}
                  onChange={(e) => updatePackage(i, { description: e.target.value })}
                />
                <div className="grid gap-3 sm:grid-cols-3">
                  <div>
                    <Label>{t("mpsell.edit.price")}</Label>
                    <Input
                      type="number"
                      min={5}
                      value={p.priceUsd}
                      onChange={(e) => updatePackage(i, { priceUsd: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>{t("mpsell.edit.deliveryDays")}</Label>
                    <Input
                      type="number"
                      min={1}
                      value={p.deliveryDays}
                      onChange={(e) => updatePackage(i, { deliveryDays: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>{t("mpsell.edit.revisions")}</Label>
                    <Input
                      type="number"
                      min={0}
                      value={p.revisions}
                      onChange={(e) => updatePackage(i, { revisions: e.target.value })}
                    />
                  </div>
                </div>
                <Textarea
                  rows={3}
                  placeholder={t("mpsell.edit.deliverablesPlaceholder")}
                  value={p.features}
                  onChange={(e) => updatePackage(i, { features: e.target.value })}
                />
              </div>
            ))}
          </section>

          <section className="card-surface space-y-4 p-5">
            <h2 className="font-display text-lg font-bold">{t("mpsell.edit.media")}</h2>
            <div>
              <Label>{t("mpsell.edit.coverImage")}</Label>
              <div className="mt-2 flex items-center gap-3">
                {coverUrl && (
                  <img
                    src={previews[coverUrl] ?? coverUrl}
                    alt="Service cover"
                    className="h-20 w-32 rounded-lg border border-border object-cover"
                  />
                )}
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 text-sm">
                  {uploading === "cover" ? <Loader2 className="size-4 animate-spin" /> : <ImagePlus className="size-4" />}
                  Upload
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => pickImage(e.target.files, "cover")}
                  />
                </label>
              </div>
            </div>
            <div>
              <Label>{t("mpsell.edit.gallery")}</Label>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                {gallery.map((g) => (
                  <div key={g} className="relative">
                    <img
                      src={previews[g] ?? g}
                      alt="Service gallery"
                      className="h-20 w-32 rounded-lg border border-border object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => setGallery((prev) => prev.filter((x) => x !== g))}
                      className="absolute -right-2 -top-2 rounded-full bg-background p-1 shadow"
                      aria-label="Remove image"
                    >
                      <X className="size-3" />
                    </button>
                  </div>
                ))}
                {gallery.length < 6 && (
                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 text-sm">
                    {uploading === "gallery" ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <ImagePlus className="size-4" />
                    )}
                    {t("mpsell.edit.add")}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => pickImage(e.target.files, "gallery")}
                    />
                  </label>
                )}
              </div>
            </div>
          </section>

          <section className="card-surface space-y-3 p-5">
            <h2 className="font-display text-lg font-bold">{t("mpsell.edit.buyerRequirements")}</h2>
            {requirements.map((r, i) => (
              <div key={i} className="flex gap-2">
                <Input
                  value={r}
                  placeholder={t("mpsell.edit.requirementPlaceholder")}
                  onChange={(e) =>
                    setRequirements((prev) => prev.map((x, idx) => (idx === i ? e.target.value : x)))
                  }
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setRequirements((prev) => prev.filter((_, idx) => idx !== i))}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => setRequirements((p) => [...p, ""])}>
              <Plus className="size-4" /> {t("mpsell.edit.addRequirement")}
            </Button>
          </section>

          <div className="flex flex-wrap gap-3">
            <Button variant="hot" disabled={save.isPending} onClick={() => save.mutate(true)}>
              {save.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
              {t("mpsell.edit.saveSubmit")}
            </Button>
            <Button variant="outline" disabled={save.isPending} onClick={() => save.mutate(false)}>
              {t("mpsell.edit.saveDraft")}
            </Button>
            <Button variant="ghost" onClick={() => navigate({ to: "/marketplace/sell" })}>
              {t("mpsell.edit.cancel")}
            </Button>
          </div>
        </div>

        <ImageCropDialog
          file={cropFile}
          open={cropOpen}
          onOpenChange={setCropOpen}
          round={false}
          aspect={cropKind === "cover" ? 16 / 9 : 4 / 3}
          onCropped={(f) => handleUpload(f, cropKind)}
        />
      </div>
    </MarketplaceGate>
  );
}
